import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const ACCENT = '#A67C3A';
const DEEP_GOLD = '#8B6914';

const MENU_ITEMS = [
  { label: 'My Profile', path: '/profile' },
  { label: 'Journal', path: '/journal' },
  { label: 'Journey', path: '/journey' },
];

export default function NavMenu({ variant = 'light' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  // Close on outside tap
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (buttonRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Close on route change
  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const isDark = variant === 'dark';
  const iconColor = isDark ? 'rgba(255,255,255,0.9)' : ACCENT;
  const borderColor = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(166,124,58,0.3)';

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(o => !o)}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: `1px solid ${borderColor}`,
          borderRadius: 22,
          color: iconColor,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {isOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 200,
            background: PAPER,
            border: '1px solid rgba(166,124,58,0.3)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {MENU_ITEMS.map((item, idx) => {
            const isCurrent = location.pathname === item.path;
            const isLast = idx === MENU_ITEMS.length - 1;
            return (
              <button
                key={item.path}
                role="menuitem"
                disabled={isCurrent}
                onClick={() => {
                  if (!isCurrent) navigate(item.path);
                  setIsOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '14px 18px',
                  textAlign: 'left',
                  background: isCurrent ? 'rgba(166,124,58,0.08)' : 'transparent',
                  border: 'none',
                  borderBottom: isLast ? 'none' : '1px solid rgba(166,124,58,0.15)',
                  color: isCurrent ? DEEP_GOLD : INK,
                  fontFamily: 'Georgia, serif',
                  fontSize: 15,
                  fontWeight: isCurrent ? 600 : 400,
                  cursor: isCurrent ? 'default' : 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
